import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  PhotoCamera as CameraIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Crop as CropIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  DeleteForever as DeleteForeverIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  LocationOn as LocationIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Pinterest as PinterestIcon,
  YouTube as YouTubeIcon,
  GitHub as GitHubIcon,
  MusicNote as TikTokIcon,
  X as XIcon,
  Work as WorkIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import ReactCrop, { centerCrop, makeAspectCrop, Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './ProfileEditModal.css';
import { motion, AnimatePresence } from 'framer-motion';
import { UserService } from '../../services/userService';
import { ProfileImageService } from '../../services/profileImageService';
import { AddressService, Address } from '../../services/addressService';
import UserAvatar from './UserAvatar';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';

interface ProfileEditModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ProfileData) => void;
  onImageDeleted?: (type: 'avatar' | 'banner') => void;
  initialData: ProfileData;
}

interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  location: string;
  bio: string;
  website: string;
  avatar: string;
  coverImage: string;
  interests: string[];
  isPublic: boolean;
  notifications: boolean;
  alias?: string;
  socialMediaLinks?: { [key: string]: string };
  selectedAddressId?: string;
  workExperience?: string;
  education?: string;
}

// Social media platforms configuration
const SOCIAL_MEDIA_PLATFORMS = {
  linkedin: { name: 'LinkedIn', icon: LinkedInIcon, placeholder: 'https://linkedin.com/in/username' },
  twitter: { name: 'Twitter/X', icon: XIcon, placeholder: 'https://twitter.com/username' },
  facebook: { name: 'Facebook', icon: FacebookIcon, placeholder: 'https://facebook.com/username' },
  instagram: { name: 'Instagram', icon: InstagramIcon, placeholder: 'https://instagram.com/username' },
  pinterest: { name: 'Pinterest', icon: PinterestIcon, placeholder: 'https://pinterest.com/username' },
  youtube: { name: 'YouTube', icon: YouTubeIcon, placeholder: 'https://youtube.com/@username' },
  github: { name: 'GitHub', icon: GitHubIcon, placeholder: 'https://github.com/username' },
  tiktok: { name: 'TikTok', icon: TikTokIcon, placeholder: 'https://tiktok.com/@username' },
};

const ProfileEditModal: React.FC<ProfileEditModalProps> = ({
  open,
  onClose,
  onSave,
  onImageDeleted,
  initialData,
}) => {
  const { user } = useAuthStore();
  const [profileData, setProfileData] = useState<ProfileData>(initialData);
  const [activeTab, setActiveTab] = useState<'basic' | 'images' | 'preferences' | 'professional'>('basic');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImage, setCropImage] = useState<string>('');
  const [cropType, setCropType] = useState<'avatar' | 'cover'>('avatar');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [newInterest, setNewInterest] = useState('');
  const [imgRef, setImgRef] = useState<HTMLImageElement | null>(null);
  
  // Social media and addresses state
  const [userAddresses, setUserAddresses] = useState<Address[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [newSocialPlatform, setNewSocialPlatform] = useState('');
  
  // Alias-related state
  const [aliasInfo, setAliasInfo] = useState<{
    alias: string | null;
    alias_last_changed: string | null;
    can_change: boolean;
  } | null>(null);
  const [loadingAlias, setLoadingAlias] = useState(false);
  const [aliasError, setAliasError] = useState<string | null>(null);
  
  // Delete operations state
  const [deletingProfilePicture, setDeletingProfilePicture] = useState(false);
  const [deletingBannerImage, setDeletingBannerImage] = useState(false);
  
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  // Fetch alias info when modal opens
  useEffect(() => {
    const fetchAliasInfo = async () => {
      if (open && user?.id) {
        setLoadingAlias(true);
        setAliasError(null);
        try {
          const info = await UserService.getUserAliasInfo(user.id);
          setAliasInfo(info);
          if (info?.alias) {
            setProfileData(prev => ({ ...prev, alias: info.alias || undefined }));
          }
        } catch (error) {
          console.error('Error fetching alias info:', error);
          setAliasError('Failed to load alias information');
        } finally {
          setLoadingAlias(false);
        }
      }
    };

    fetchAliasInfo();
  }, [open, user?.id]);

  // Fetch existing profile images when modal opens
  useEffect(() => {
    const fetchProfileImages = async () => {
      if (open && user?.id) {
        try {
          const profileImages = await ProfileImageService.getUserProfileImages(user.id);
          if (profileImages) {
            setProfileData(prev => ({
              ...prev,
              avatar: profileImages.profile_picture_url || prev.avatar,
              coverImage: profileImages.banner_image_url || prev.coverImage
            }));
          }
        } catch (error) {
          console.error('Error fetching profile images:', error);
          // Don't show error to user as this is not critical
        }
      }
    };

    fetchProfileImages();
  }, [open, user?.id]);

  // Fetch core user profile fields when modal opens
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (open && user?.id) {
        try {
          const profile = await UserService.getUserProfile(user.id);
          if (profile) {
            // Normalize possibly stringified fields
            let socialLinks: any = profile.social_media_links;
            if (typeof socialLinks === 'string') {
              try { socialLinks = JSON.parse(socialLinks); } catch { socialLinks = {}; }
            }
            let interests: any = profile.interests;
            if (typeof interests === 'string') {
              try { interests = JSON.parse(interests); } catch { interests = interests ? [interests] : []; }
            }
            if (!Array.isArray(interests)) interests = [];

            setProfileData(prev => ({
              ...prev,
              firstName: profile.first_name || prev.firstName || '',
              lastName: profile.last_name || prev.lastName || '',
              email: prev.email || profile.email || '',
              location: profile.location || prev.location || '',
              bio: profile.bio || prev.bio || '',
              website: profile.website || prev.website || '',
              socialMediaLinks: socialLinks || prev.socialMediaLinks || {},
              interests: interests || prev.interests || [],
              workExperience: (profile.work_experience as any) || prev.workExperience || '',
              education: (profile.education as any) || prev.education || '',
            }));
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      }
    };

    fetchUserProfile();
  }, [open, user?.id]);

  // Fetch user addresses when modal opens
  useEffect(() => {
    const fetchAddresses = async () => {
      if (open && user?.id) {
        setLoadingAddresses(true);
        try {
          const addresses = await AddressService.getUserAddresses();
          setUserAddresses(addresses);
        } catch (error) {
          console.error('Error fetching addresses:', error);
          toast.error('Failed to load addresses');
        } finally {
          setLoadingAddresses(false);
        }
      }
    };

    fetchAddresses();
  }, [open, user?.id]);

  const handleInputChange = (field: keyof ProfileData, value: any) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const handleAliasChange = async (newAlias: string) => {
    if (!user?.id || !aliasInfo?.can_change) {
      return;
    }

    setLoadingAlias(true);
    setAliasError(null);

    try {
      const result = await UserService.updateUserAlias(user.id, newAlias);
      
      if (result.success) {
        setProfileData(prev => ({ ...prev, alias: newAlias }));
        // Refresh alias info
        const updatedInfo = await UserService.getUserAliasInfo(user.id);
        setAliasInfo(updatedInfo);
        toast.success('Alias updated successfully!');
      } else {
        setAliasError(result.error || 'Failed to update alias');
        toast.error(result.error || 'Failed to update alias');
      }
    } catch (error) {
      console.error('Error updating alias:', error);
      setAliasError('Failed to update alias');
      toast.error('Failed to update alias');
    } finally {
      setLoadingAlias(false);
    }
  };

  const handleImageUpload = (type: 'avatar' | 'cover') => {
    const input = type === 'avatar' ? avatarInputRef.current : coverInputRef.current;
    if (input) {
      input.click();
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>, type: 'avatar' | 'cover') => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string;
        setCropImage(imageUrl);
        setCropType(type);
        
        // Initialize crop area based on type
        const aspectRatio = type === 'avatar' ? 1 : 16/9;
        const crop = centerCrop(
          makeAspectCrop(
            {
              unit: '%',
              width: 90,
            },
            aspectRatio,
            400,
            400
          ),
          400,
          400
        );
        setCrop(crop);
        setCompletedCrop(undefined);
        setScale(1);
        setRotate(0);
        setCropModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const onImageLoad = (img: HTMLImageElement) => {
    setImgRef(img);
    const { width, height } = img;
    const aspectRatio = cropType === 'avatar' ? 1 : 16/9;
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspectRatio,
        width,
        height
      ),
      width,
      height
    );
    setCrop(crop);
  };

  const applyCrop = async () => {
    if (imgRef && completedCrop) {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) return;

      const scaleX = imgRef.naturalWidth / imgRef.width;
      const scaleY = imgRef.naturalHeight / imgRef.height;
      
      canvas.width = completedCrop.width;
      canvas.height = completedCrop.height;
      
      ctx.scale(scale, scale);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      const cropX = completedCrop.x * scaleX;
      const cropY = completedCrop.y * scaleY;
      
      ctx.drawImage(
        imgRef,
        cropX,
        cropY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        completedCrop.width,
        completedCrop.height
      );
      
      const croppedImageUrl = canvas.toDataURL('image/jpeg', 0.9);
      
      if (cropType === 'avatar') {
        handleInputChange('avatar', croppedImageUrl);
      } else {
        handleInputChange('coverImage', croppedImageUrl);
      }
      
      setCropModalOpen(false);
      setCropImage('');
      setCrop(undefined);
      setCompletedCrop(undefined);
    }
  };

  const handleAddInterest = () => {
    if (newInterest.trim() && !profileData.interests.includes(newInterest.trim())) {
      handleInputChange('interests', [...profileData.interests, newInterest.trim()]);
      setNewInterest('');
    }
  };

  const handleRemoveInterest = (interest: string) => {
    handleInputChange('interests', profileData.interests.filter(i => i !== interest));
  };

  // Social media handlers
  const handleAddSocialMedia = (platform: string) => {
    if (platform && !profileData.socialMediaLinks?.[platform]) {
      const currentLinks = profileData.socialMediaLinks || {};
      handleInputChange('socialMediaLinks', { ...currentLinks, [platform]: '' });
    }
  };

  const handleRemoveSocialMedia = (platform: string) => {
    if (profileData.socialMediaLinks) {
      const newLinks = { ...profileData.socialMediaLinks };
      delete newLinks[platform];
      handleInputChange('socialMediaLinks', newLinks);
    }
  };

  const handleSocialMediaChange = (platform: string, value: string) => {
    const currentLinks = profileData.socialMediaLinks || {};
    handleInputChange('socialMediaLinks', { ...currentLinks, [platform]: value });
  };

  const handleAddressChange = (addressId: string) => {
    const selectedAddress = userAddresses.find(addr => addr.id === addressId);
    if (selectedAddress) {
      const locationString = `${selectedAddress.city}, ${selectedAddress.state}, ${selectedAddress.country}`;
      handleInputChange('location', locationString);
      handleInputChange('selectedAddressId', addressId);
    }
  };

  const handleDeleteProfilePicture = async () => {
    if (!user?.id) {
      toast.error('You must be logged in to delete profile picture');
      return;
    }

    setDeletingProfilePicture(true);
    try {
      await ProfileImageService.deleteProfilePicture(user.id);
      handleInputChange('avatar', '');
      onImageDeleted?.('avatar');
      toast.success('Profile picture deleted successfully!');
    } catch (error) {
      console.error('Error deleting profile picture:', error);
      toast.error('Failed to delete profile picture');
    } finally {
      setDeletingProfilePicture(false);
    }
  };

  const handleDeleteBannerImage = async () => {
    if (!user?.id) {
      toast.error('You must be logged in to delete banner image');
      return;
    }

    setDeletingBannerImage(true);
    try {
      await ProfileImageService.deleteBannerImage(user.id);
      handleInputChange('coverImage', '');
      onImageDeleted?.('banner');
      toast.success('Banner image deleted successfully!');
    } catch (error) {
      console.error('Error deleting banner image:', error);
      toast.error('Failed to delete banner image');
    } finally {
      setDeletingBannerImage(false);
    }
  };

  const handleSave = async () => {
    if (!user) {
      toast.error('You must be logged in to save profile changes');
      return;
    }

    setIsSaving(true);
    try {
      let profilePictureUrl = profileData.avatar;
      let bannerImageUrl = profileData.coverImage;

      // Upload profile picture if it's a data URL (newly uploaded)
      if (profileData.avatar && profileData.avatar.startsWith('data:image/')) {
        try {
          // Convert data URL to file
          const response = await fetch(profileData.avatar);
          const blob = await response.blob();
          const file = new File([blob], 'profile-picture.jpg', { type: 'image/jpeg' });
          
          const { url } = await ProfileImageService.uploadProfilePicture(file, user.id);
          profilePictureUrl = url;
          toast.success('Profile picture uploaded successfully!');
        } catch (error) {
          console.error('Error uploading profile picture:', error);
          toast.error('Failed to upload profile picture');
          return;
        }
      }

      // Upload banner image if it's a data URL (newly uploaded)
      if (profileData.coverImage && profileData.coverImage.startsWith('data:image/')) {
        try {
          // Convert data URL to file
          const response = await fetch(profileData.coverImage);
          const blob = await response.blob();
          const file = new File([blob], 'banner-image.jpg', { type: 'image/jpeg' });
          
          const { url } = await ProfileImageService.uploadBannerImage(file, user.id);
          bannerImageUrl = url;
          toast.success('Banner image uploaded successfully!');
        } catch (error) {
          console.error('Error uploading banner image:', error);
          toast.error('Failed to upload banner image');
          return;
        }
      }

      // Save profile images to database
      if (profilePictureUrl || bannerImageUrl) {
        await ProfileImageService.saveProfileImages(user.id, profilePictureUrl, bannerImageUrl);
      }

      // Prepare profile data for database update
      const profileUpdateData = {
        bio: profileData.bio,
        location: profileData.location,
        website: profileData.website,
        social_media_links: profileData.socialMediaLinks || {},
        interests: profileData.interests,
        is_public: profileData.isPublic,
        notifications_enabled: profileData.notifications,
        selected_address_id: profileData.selectedAddressId,
        first_name: profileData.firstName || '',
        last_name: profileData.lastName || '',
        work_experience: profileData.workExperience || '',
        education: profileData.education || '',
      };

      // Update user profile in database
      const result = await UserService.updateUserProfileDirect(user.id, profileUpdateData);
      
      if (!result.success) {
        toast.error(result.error || 'Failed to save profile changes');
        return;
      }

      // Update profile data with the new URLs
      const updatedProfileData = {
        ...profileData,
        avatar: profilePictureUrl,
        coverImage: bannerImageUrl
      };

      onSave(updatedProfileData);
      onClose();
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile changes');
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: <EditIcon /> },
    { id: 'images', label: 'Images', icon: <CameraIcon /> },
    { id: 'professional', label: 'Professional Background', icon: <WorkIcon /> },
    { id: 'preferences', label: 'Preferences', icon: <SaveIcon /> },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" fontWeight="bold">
              Edit Profile
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Tab Navigation */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {tabs.map((tab) => (
                <Button
                  key={tab.id}
                  variant={activeTab === tab.id ? 'contained' : 'text'}
                  onClick={() => setActiveTab(tab.id as any)}
                  startIcon={tab.icon}
                  sx={{
                    textTransform: 'none',
                    fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>
          </Box>

          <Box sx={{ p: 3, maxHeight: '60vh', overflow: 'auto' }}>
            <AnimatePresence mode="wait">
              {activeTab === 'basic' && (
                <motion.div
                  key="basic"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Alias Field */}
                    <Box>
                      <TextField
                        label="Username Alias"
                        value={profileData.alias || ''}
                        onChange={(e) => handleInputChange('alias', e.target.value)}
                        onBlur={(e) => {
                          const newAlias = e.target.value.trim();
                          if (newAlias && newAlias !== aliasInfo?.alias) {
                            handleAliasChange(newAlias);
                          }
                        }}
                        fullWidth
                        variant="outlined"
                        placeholder="Enter your unique username"
                        disabled={loadingAlias || !aliasInfo?.can_change}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              @
                            </InputAdornment>
                          ),
                          endAdornment: loadingAlias ? (
                            <CircularProgress size={20} />
                          ) : aliasInfo?.can_change ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <WarningIcon color="warning" />
                          ),
                        }}
                        helperText={
                          aliasInfo?.can_change 
                            ? "You can change your alias once per month"
                            : aliasInfo?.alias_last_changed
                            ? `Last changed: ${new Date(aliasInfo.alias_last_changed).toLocaleDateString()}. Next change available: ${new Date(new Date(aliasInfo.alias_last_changed).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}`
                            : "Loading alias information..."
                        }
                      />
                      
                      {aliasError && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {aliasError}
                        </Alert>
                      )}
                      
                      {!aliasInfo?.can_change && aliasInfo?.alias_last_changed && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            <strong>Monthly Limit:</strong> You can only change your alias once per month. 
                            Your next change will be available on{' '}
                            {new Date(new Date(aliasInfo.alias_last_changed).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}.
                          </Typography>
                        </Alert>
                      )}
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <TextField
                        label="First Name"
                        value={profileData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        fullWidth
                        variant="outlined"
                      />
                      <TextField
                        label="Last Name"
                        value={profileData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        fullWidth
                        variant="outlined"
                      />
                    </Box>
                    
                    <TextField
                      label="Email"
                      value={profileData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      fullWidth
                      variant="outlined"
                      type="email"
                    />
                    
                    {/* Location Selection */}
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Location
                      </Typography>
                      {userAddresses.length > 0 ? (
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Select from your addresses</InputLabel>
                          <Select
                            value={profileData.selectedAddressId || ''}
                            onChange={(e) => handleAddressChange(e.target.value)}
                            label="Select from your addresses"
                            startAdornment={
                              <InputAdornment position="start">
                                <LocationIcon />
                              </InputAdornment>
                            }
                          >
                            <MenuItem value="">
                              <em>Custom location</em>
                            </MenuItem>
                            {userAddresses.map((address) => (
                              <MenuItem key={address.id} value={address.id}>
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    {address.title}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {address.city}, {address.state}, {address.country}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          No addresses found. Add addresses in your account settings to use them here.
                        </Alert>
                      )}
                      
                      {/* Custom location input */}
                      <TextField
                        label="Custom Location"
                        value={profileData.location}
                        onChange={(e) => handleInputChange('location', e.target.value)}
                        fullWidth
                        variant="outlined"
                        placeholder="City, Country"
                        sx={{ mt: 2 }}
                      />
                    </Box>
                    
                    <TextField
                      label="Bio"
                      value={profileData.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      fullWidth
                      multiline
                      rows={4}
                      variant="outlined"
                      placeholder="Tell us about yourself..."
                    />
                    
                    <TextField
                      label="Website"
                      value={profileData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      fullWidth
                      variant="outlined"
                      placeholder="https://yourwebsite.com"
                    />
                    
                    {/* Social Media Links */}
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Social Media Links
                      </Typography>
                      
                      {/* Existing social media links */}
                      {profileData.socialMediaLinks && Object.keys(profileData.socialMediaLinks).map((platform) => {
                        const platformConfig = SOCIAL_MEDIA_PLATFORMS[platform as keyof typeof SOCIAL_MEDIA_PLATFORMS];
                        if (!platformConfig) return null;
                        
                        const IconComponent = platformConfig.icon;
                        return (
                          <Box key={platform} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <IconComponent sx={{ color: 'primary.main' }} />
                            <TextField
                              label={platformConfig.name}
                              value={profileData.socialMediaLinks![platform] || ''}
                              onChange={(e) => handleSocialMediaChange(platform, e.target.value)}
                              fullWidth
                              variant="outlined"
                              placeholder={platformConfig.placeholder}
                              size="small"
                            />
                            <IconButton
                              onClick={() => handleRemoveSocialMedia(platform)}
                              color="error"
                              size="small"
                            >
                              <RemoveIcon />
                            </IconButton>
                          </Box>
                        );
                      })}
                      
                      {/* Add new social media platform */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                        <FormControl sx={{ minWidth: 200 }}>
                          <InputLabel>Add Platform</InputLabel>
                          <Select
                            value={newSocialPlatform}
                            onChange={(e) => setNewSocialPlatform(e.target.value)}
                            label="Add Platform"
                          >
                            {Object.entries(SOCIAL_MEDIA_PLATFORMS).map(([key, config]) => (
                              <MenuItem 
                                key={key} 
                                value={key}
                                disabled={profileData.socialMediaLinks?.[key] !== undefined}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <config.icon />
                                  {config.name}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            if (newSocialPlatform) {
                              handleAddSocialMedia(newSocialPlatform);
                              setNewSocialPlatform('');
                            }
                          }}
                          disabled={!newSocialPlatform}
                        >
                          Add
                        </Button>
                      </Box>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Interests & Categories
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {profileData.interests.map((interest, index) => (
                          <Chip
                            key={index}
                            label={interest}
                            onDelete={() => handleRemoveInterest(interest)}
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          value={newInterest}
                          onChange={(e) => setNewInterest(e.target.value)}
                          placeholder="Add interest..."
                          size="small"
                          onKeyPress={(e) => e.key === 'Enter' && handleAddInterest()}
                        />
                        <Button
                          variant="outlined"
                          onClick={handleAddInterest}
                          disabled={!newInterest.trim()}
                        >
                          Add
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </motion.div>
              )}

              {activeTab === 'images' && (
                <motion.div
                  key="images"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {/* Cover Image */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold">
                          Cover Image
                        </Typography>
                        {profileData.coverImage && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={deletingBannerImage ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
                            onClick={handleDeleteBannerImage}
                            disabled={deletingBannerImage}
                            sx={{ minWidth: 'auto' }}
                          >
                            {deletingBannerImage ? 'Deleting...' : 'Delete'}
                          </Button>
                        )}
                      </Box>
                      <Box
                        sx={{
                          position: 'relative',
                          height: 200,
                          borderRadius: 2,
                          overflow: 'hidden',
                          backgroundImage: `url(${profileData.coverImage})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          border: '2px dashed',
                          borderColor: 'grey.300',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: 'primary.main',
                            '& .overlay': {
                              opacity: 1,
                            },
                          },
                        }}
                        onClick={() => handleImageUpload('cover')}
                      >
                        <Box
                          className="overlay"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.3s',
                          }}
                        >
                          <Box sx={{ textAlign: 'center', color: 'white' }}>
                            <CameraIcon sx={{ fontSize: 48, mb: 1 }} />
                            <Typography variant="h6" fontWeight="bold">
                              Click to change cover image
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <input
                        ref={coverInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileSelect(e, 'cover')}
                      />
                    </Box>

                    {/* Avatar */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold">
                          Profile Picture
                        </Typography>
                        {profileData.avatar && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={deletingProfilePicture ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
                            onClick={handleDeleteProfilePicture}
                            disabled={deletingProfilePicture}
                            sx={{ minWidth: 'auto' }}
                          >
                            {deletingProfilePicture ? 'Deleting...' : 'Delete'}
                          </Button>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Box
                          sx={{
                            position: 'relative',
                            cursor: 'pointer',
                            '&:hover .overlay': {
                              opacity: 1,
                            },
                          }}
                          onClick={() => handleImageUpload('avatar')}
                        >
                          <UserAvatar
                            src={profileData.avatar}
                            name={`${profileData.firstName} ${profileData.lastName}`.trim()}
                            email={profileData.email}
                            size={120}
                            sx={{
                              border: '4px solid white',
                              boxShadow: 3,
                            }}
                          />
                          <Box
                            className="overlay"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              borderRadius: '50%',
                              bgcolor: 'rgba(0,0,0,0.5)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 0,
                              transition: 'opacity 0.3s',
                            }}
                          >
                            <CameraIcon sx={{ color: 'white', fontSize: 32 }} />
                          </Box>
                        </Box>
                        <Box>
                          <Typography variant="body1" fontWeight="bold" gutterBottom>
                            Profile Picture
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Click on the image to change your profile picture. Recommended size: 400x400px
                          </Typography>
                          <Button
                            variant="outlined"
                            startIcon={<CameraIcon />}
                            onClick={() => handleImageUpload('avatar')}
                          >
                            Change Photo
                          </Button>
                        </Box>
                        <input
                          ref={avatarInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => handleFileSelect(e, 'avatar')}
                        />
                      </Box>
                    </Box>
                  </Box>
                </motion.div>
              )}

              {activeTab === 'professional' && (
                <motion.div
                  key="professional"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>
                      Your Professional Background
                    </Typography>
                    
                    <TextField
                      label="Work Experience"
                      multiline
                      rows={6}
                      value={profileData.workExperience || ''}
                      onChange={(e) => handleInputChange('workExperience', e.target.value)}
                      placeholder="Describe your relevant work experience, including key achievements and responsibilities..."
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <WorkIcon sx={{ color: '#6b7280' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#3b82f6',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#3b82f6',
                          },
                        },
                      }}
                    />
                    
                    <TextField
                      label="Education"
                      multiline
                      rows={4}
                      value={profileData.education || ''}
                      onChange={(e) => handleInputChange('education', e.target.value)}
                      placeholder="Your educational background, degrees, certifications..."
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SchoolIcon sx={{ color: '#6b7280' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#3b82f6',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#3b82f6',
                          },
                        },
                      }}
                    />
                    
                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>Professional Information:</strong> This information will be used to enhance your profile 
                        and can be helpful for job applications and professional networking.
                      </Typography>
                    </Alert>
                  </Box>
                </motion.div>
              )}

              {activeTab === 'preferences' && (
                <motion.div
                  key="preferences"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={profileData.isPublic}
                          onChange={(e) => handleInputChange('isPublic', e.target.checked)}
                        />
                      }
                      label="Make profile public"
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={profileData.notifications}
                          onChange={(e) => handleInputChange('notifications', e.target.checked)}
                        />
                      }
                      label="Enable notifications"
                    />
                    
                    <Divider />
                    
                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>Privacy Note:</strong> Making your profile public means other users can see your information and products. 
                        You can change this setting at any time.
                      </Typography>
                    </Alert>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            startIcon={<CancelIcon />}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={isSaving}
            sx={{ px: 4 }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Crop Modal */}
      <Dialog
        open={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              Crop {cropType === 'avatar' ? 'Profile Picture' : 'Cover Image'}
            </Typography>
            <IconButton onClick={() => setCropModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ position: 'relative', height: 500, bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {cropImage && (
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={cropType === 'avatar' ? 1 : 16/9}
                minWidth={cropType === 'avatar' ? 100 : 200}
                minHeight={cropType === 'avatar' ? 100 : 112}
                className="react-crop"
              >
                <img
                  ref={setImgRef}
                  alt="Crop me"
                  src={cropImage}
                  style={{ 
                    transform: `scale(${scale}) rotate(${rotate}deg)`,
                    maxHeight: '100%',
                    maxWidth: '100%',
                  }}
                  onLoad={(e) => onImageLoad(e.currentTarget)}
                />
              </ReactCrop>
            )}
          </Box>
          
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Crop Controls
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Scale:</Typography>
                <IconButton 
                  size="small" 
                  onClick={() => setScale(prev => Math.max(0.5, prev - 0.1))}
                >
                  <ZoomOutIcon />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'center' }}>
                  {Math.round(scale * 100)}%
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => setScale(prev => Math.min(3, prev + 0.1))}
                >
                  <ZoomInIcon />
                </IconButton>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Rotate:</Typography>
                <IconButton 
                  size="small" 
                  onClick={() => setRotate(prev => prev - 90)}
                >
                  <RotateLeftIcon />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={() => setRotate(prev => prev + 90)}
                >
                  <RotateRightIcon />
                </IconButton>
              </Box>
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              Drag the corners to adjust the crop area. {cropType === 'avatar' ? 'Keep it square for best results.' : 'Maintain the 16:9 aspect ratio.'}
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setCropModalOpen(false)}
            variant="outlined"
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            onClick={applyCrop}
            variant="contained"
            startIcon={<CropIcon />}
          >
            Apply Crop
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfileEditModal;
